import { useState, useEffect, useRef } from "react";
import { MapGoogle } from "../components/map";
import { LinhasOnibus, PartidasLinha } from "../features/lines";
import { PartidasParada } from "../features/stops";
import { FloatingNav } from "../components/navigation";
import { BottomSheet } from "../components/ui";
import { useVehicleTracking } from "../hooks/useVehicleTracking";
import { busApi } from "../services/api/busApi";
import "../styles/App.css";

function PlannerPage() {
  const [activeTab, setActiveTab] = useState(null);
  const [sheetContent, setSheetContent] = useState(null);
  const [sheetHeight, setSheetHeight] = useState("50%");
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedRouteShortName, setSelectedRouteShortName] = useState(null);
  const [selectedStopId, setSelectedStopId] = useState(null);
  const [centerMapPosition, setCenterMapPosition] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [focusVehicle, setFocusVehicle] = useState(null);
  const [focusedVehicleId, setFocusedVehicleId] = useState(null);
  const [trackingRouteShortNames, setTrackingRouteShortNames] = useState([]);
  // trackingMeta é write-only (meta operacional não renderizada). Binding do valor
  // descartado p/ evitar warning no-unused-vars; remoção total em T20. Ver §6 da análise.
  const [, setTrackingMeta] = useState({
    source: null,
    degraded: false,
    warning: null,
    lastSuccessfulAt: null,
  });

  const [userLocation, setUserLocation] = useState(null);
  const [nearbyStops, setNearbyStops] = useState([]);
  const [nearbyStopsLoading, setNearbyStopsLoading] = useState(false);
  const [nearbyStopsError, setNearbyStopsError] = useState(null);

  const [tripOverlay, setTripOverlay] = useState(null);
  const [tripOverlayLoading, setTripOverlayLoading] = useState(false);
  const [tripOverlayError, setTripOverlayError] = useState(null);
  const tripOverlayCacheRef = useRef(new Map());

  const focusVehicleTimeoutRef = useRef(null);
  const clearFocusVehicleTimeoutRef = useRef(null);

  const getOperationalDate = (date = new Date()) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Polling global: mantém atualização quando o BottomSheet está fechado,
  // para o veículo não “congelar” no mapa após clicar em "Ver no mapa".
  const shouldTrackInBackground =
    sheetContent === null && trackingRouteShortNames.length > 0;

  const backgroundTrackingFilters =
    trackingRouteShortNames.length > 0
      ? {
          // line nos endpoints operacionais = route_short_name (nunca routeId)
          line: trackingRouteShortNames[0],
        }
      : {};

  const {
    vehicles: trackedVehicles,
    source: trackedSource,
    degraded: trackedDegraded,
    warning: trackedWarning,
    lastSuccessfulAt: trackedLastSuccessfulAt,
  } = useVehicleTracking(
    backgroundTrackingFilters,
    30000,
    shouldTrackInBackground
  );

  useEffect(() => {
    if (!shouldTrackInBackground) return;
    if (!trackedVehicles.length) {
      setVehicles([]);
      return;
    }

    const filtered = trackedVehicles.filter((v) =>
      trackingRouteShortNames.includes(v.routeShortName)
    );
    setVehicles(filtered);

    // Se ainda estiver focado em algum veículo, manter posição mais recente
    if (focusedVehicleId) {
      const updatedFocused = trackedVehicles.find(
        (v) => v.vehicleId === focusedVehicleId
      );
      if (updatedFocused) {
        setFocusVehicle(updatedFocused);
      }
    }
  }, [
    shouldTrackInBackground,
    trackedVehicles,
    trackingRouteShortNames,
    focusedVehicleId,
  ]);

  useEffect(() => {
    if (!shouldTrackInBackground) return;
    setTrackingMeta({
      source: trackedSource,
      degraded: trackedDegraded,
      warning: trackedWarning,
      lastSuccessfulAt: trackedLastSuccessfulAt,
    });
  }, [
    shouldTrackInBackground,
    trackedSource,
    trackedDegraded,
    trackedWarning,
    trackedLastSuccessfulAt,
  ]);

  const handleTabChange = (tab) => {
    if (activeTab === tab) {
      setActiveTab(null);
      setSheetContent(null);
      return;
    }

    setActiveTab(tab);
    setSelectedRouteId(null);
    setSelectedRouteShortName(null);

    if (tab === "linhas") {
      setSheetContent("linhas");
      setSheetHeight("60%");
    } else if (tab === "info") {
      setSheetContent("info");
      setSheetHeight("40%");
    }
  };

  const handleLinhaSelect = (linha) => {
    setSelectedRouteId(linha?.routeId ?? null);
    setSelectedRouteShortName(linha?.shortName ?? null);
    setSheetContent("horarios");
    setSheetHeight("70%");
  };

  const handleBackToLinhas = () => {
    setSelectedRouteId(null);
    setSelectedRouteShortName(null);
    setSheetContent("linhas");
    setSheetHeight("60%");
  };

  const handleMarkerClick = (stopId) => {
    setSelectedStopId(stopId);
    setSheetContent("ponto");
    setSheetHeight("40%");
    setActiveTab(null);
  };

  const handleCloseSheet = () => {
    setActiveTab(null);
    setSheetContent(null);
    setSelectedRouteId(null);
    setSelectedRouteShortName(null);
    setSelectedStopId(null);
    setVehicles([]);
    setFocusVehicle(null);
    setFocusedVehicleId(null);
    setTrackingRouteShortNames([]);
    setTrackingMeta({
      source: null,
      degraded: false,
      warning: null,
      lastSuccessfulAt: null,
    });

    setUserLocation(null);
    setNearbyStops([]);
    setNearbyStopsLoading(false);
    setNearbyStopsError(null);

    setTripOverlay(null);
    setTripOverlayLoading(false);
    setTripOverlayError(null);
  };

  const loadTripOverlayForVehicle = async (vehicle) => {
    const normalizeIdentifier = (value) => {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      const lowered = trimmed.toLowerCase();
      if (lowered === "null" || lowered === "undefined") return null;
      return trimmed;
    };

    const shapeLookupId =
      normalizeIdentifier(vehicle?.vehiclePrefix) ||
      normalizeIdentifier(vehicle?.tripId) ||
      normalizeIdentifier(vehicle?.tripIdRaw) ||
      normalizeIdentifier(vehicle?.routeId) ||
      normalizeIdentifier(vehicle?.routeShortName) ||
      null;

    if (!shapeLookupId) {
      setTripOverlay(null);
      setTripOverlayError(
        new Error(
          "Veículo sem identificador (vehiclePrefix/tripId/route); não é possível desenhar o percurso."
        )
      );
      return;
    }

    const cached = tripOverlayCacheRef.current.get(shapeLookupId);
    if (cached) {
      setTripOverlayError(null);
      setTripOverlay(cached);
      return;
    }

    setTripOverlayError(null);
    setTripOverlayLoading(true);
    try {
      // 1) Sempre buscar o shape sem exigir tripId (robusto para feeds que vêm sem tripId)
      const res = await busApi.getTripShape(shapeLookupId, {
        includeStops: false,
      });

      const data = res?.data;
      const overlayBase = data ? { ...data, tripId: shapeLookupId } : data;
      tripOverlayCacheRef.current.set(shapeLookupId, overlayBase);
      setTripOverlay(overlayBase);

      // 2) Em seguida, buscar paradas (stops) e mesclar, para o mapa conseguir exibir as paradas.
      // Não bloqueia a renderização do shape.
      try {
        const resStops = await busApi.getTripShape(shapeLookupId, {
          includeStops: true,
        });
        const dataStops = resStops?.data;

        if (dataStops && Array.isArray(dataStops?.stops) && dataStops.stops.length > 0) {
          const mergedOverlay = {
            ...overlayBase,
            ...dataStops,
            tripId: shapeLookupId,
          };
          tripOverlayCacheRef.current.set(shapeLookupId, mergedOverlay);
          setTripOverlay(mergedOverlay);
        }
      } catch (errStops) {
        // Stops são um "extra"; se falhar, mantém o shape.
        console.warn("Não foi possível carregar paradas do percurso:", errStops);
      }
    } catch (err) {
      console.error("Erro ao buscar percurso (shape) do veículo:", err);
      setTripOverlay(null);
      setTripOverlayError(err);
    } finally {
      setTripOverlayLoading(false);
    }
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("Geolocalização não suportada neste dispositivo."));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (err) => {
          reject(err);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
      );
    });
  };

  const handleNearbyStopsClick = async () => {
    setNearbyStopsError(null);
    setNearbyStopsLoading(true);

    try {
      const loc = await getUserLocation();
      setUserLocation(loc);

      // Centraliza o mapa no usuário
      setCenterMapPosition({
        lat: loc.lat,
        lon: loc.lng,
        name: "Minha localização",
      });
      setTimeout(() => setCenterMapPosition(null), 1000);

      const radius = 500;
      const limit = 20;
      const res = await busApi.getNearbyStops({
        lat: loc.lat,
        lng: loc.lng,
        radius,
        limit,
      });
      const stops = res?.data?.stops || [];
      setNearbyStops(stops);
    } catch (err) {
      console.error("Erro ao buscar paradas perto do usuário:", err);
      setNearbyStops([]);
      setNearbyStopsError(err);
    } finally {
      setNearbyStopsLoading(false);
    }
  };

  const handleVehiclesUpdate = (updatedVehicles, meta = null) => {
    setVehicles(updatedVehicles);
    if (meta) {
      setTrackingMeta({
        source: meta.source ?? null,
        degraded: Boolean(meta.degraded),
        warning: meta.warning ?? null,
        lastSuccessfulAt: meta.lastSuccessfulAt ?? null,
      });
    }
  };

  const enrichVehicleWithOperational = async (vehicle) => {
    const prefix =
      typeof vehicle?.vehiclePrefix === "string" && vehicle.vehiclePrefix.trim()
        ? vehicle.vehiclePrefix.trim()
        : null;

    if (!prefix) return vehicle;

    try {
      const res = await busApi.getOperationalTripsByVehicle(prefix, {
        date: getOperationalDate(),
      });
      const payload = res?.data || {};
      const trips = Array.isArray(payload?.operationalTrips)
        ? payload.operationalTrips
        : Array.isArray(payload?.trips)
        ? payload.trips
        : [];
      const trip = trips[0] || null;

      if (payload?.source || payload?.warning || payload?.lastSuccessfulAt) {
        setTrackingMeta((prev) => ({
          ...prev,
          source: payload?.source ?? prev.source,
          degraded: Boolean(payload?.degraded),
          warning: payload?.warning ?? prev.warning,
          lastSuccessfulAt: payload?.lastSuccessfulAt ?? prev.lastSuccessfulAt,
        }));
      }

      if (!trip) return vehicle;

      return {
        ...vehicle,
        vehiclePrefix: prefix,
        operationalStatus: trip?.status ?? trip?.tripStatus ?? vehicle?.operationalStatus,
        punctuality: trip?.punctuality ?? trip?.tripPunctuality ?? vehicle?.punctuality,
        delayMinutes:
          trip?.delayMinutes ?? trip?.delay_minutes ?? trip?.atrasoMinutos ?? vehicle?.delayMinutes,
      };
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      if (apiMessage) {
        setTrackingMeta((prev) => ({
          ...prev,
          degraded: true,
          warning: apiMessage,
        }));
      }
      return vehicle;
    }
  };

  const handleVehicleClick = async (vehicle) => {
    // Limpar timers pendentes (evita múltiplos pans/zooms em cliques rápidos)
    if (focusVehicleTimeoutRef.current) {
      clearTimeout(focusVehicleTimeoutRef.current);
      focusVehicleTimeoutRef.current = null;
    }
    if (clearFocusVehicleTimeoutRef.current) {
      clearTimeout(clearFocusVehicleTimeoutRef.current);
      clearFocusVehicleTimeoutRef.current = null;
    }

    // Enriquecer com endpoint operacional por prefixo quando disponível
    const enrichedVehicle = await enrichVehicleWithOperational(vehicle);

    // Resetar o foco antes de refocar (permite refocar no mesmo veículo sem depender do polling)
    setFocusVehicle(null);
    setFocusedVehicleId(enrichedVehicle?.vehicleId || null);
    setTrackingRouteShortNames(
      enrichedVehicle?.routeShortName ? [enrichedVehicle.routeShortName] : []
    );

    // Buscar percurso completo + paradas (quando disponível)
    const tripPromise = loadTripOverlayForVehicle(enrichedVehicle);

    // Fechar o BottomSheet para o usuário ver o mapa
    setActiveTab(null);
    setSheetContent(null);

    // Depois que o percurso carregar, aguarda 3s e centraliza no ônibus.
    // Isso evita o comportamento atual de só centralizar quando chega o próximo polling (30s).
    Promise.resolve(tripPromise)
      .catch(() => {
        // Erro já é tratado/logado em loadTripOverlayForVehicle
      })
      .finally(() => {
        focusVehicleTimeoutRef.current = setTimeout(() => {
          setFocusVehicle(enrichedVehicle);
        }, 3000);

        // Limpar o foco logo depois para permitir refocar no mesmo veículo em novos cliques
        clearFocusVehicleTimeoutRef.current = setTimeout(() => {
          setFocusVehicle(null);
        }, 4500);
      });
  };

  useEffect(() => {
    return () => {
      if (focusVehicleTimeoutRef.current) {
        clearTimeout(focusVehicleTimeoutRef.current);
        focusVehicleTimeoutRef.current = null;
      }
      if (clearFocusVehicleTimeoutRef.current) {
        clearTimeout(clearFocusVehicleTimeoutRef.current);
        clearFocusVehicleTimeoutRef.current = null;
      }
    };
  }, []);

  // Clique no marker do ônibus no mapa: desenha percurso/paradas sem fechar BottomSheet
  const handleVehicleMarkerClick = (vehicle) => {
    loadTripOverlayForVehicle(vehicle);
  };

  const renderSheetContent = () => {
    if (sheetContent === "linhas") {
      return <LinhasOnibus onLinhaSelect={handleLinhaSelect} />;
    }
    if (sheetContent === "horarios") {
      return (
        <PartidasLinha
          routeId={selectedRouteId}
          routeShortName={selectedRouteShortName}
          onBack={handleBackToLinhas}
          onVehiclesUpdate={handleVehiclesUpdate}
          onVehicleClick={handleVehicleClick}
        />
      );
    }
    if (sheetContent === "ponto") {
      return (
        <PartidasParada stopId={selectedStopId} onClose={handleCloseSheet} />
      );
    }
    if (sheetContent === "info") {
      return (
        <div style={{ padding: "20px" }}>
          <h2>Sistema de Transporte</h2>
          <p>Embu das Artes</p>
          <p style={{ marginTop: "20px", color: "#666" }}>
            Use o mapa para visualizar pontos de ônibus e rotas.
          </p>
          <p style={{ color: "#666" }}>
            Clique em "Linhas" para ver todas as linhas disponíveis.
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="App">
      <MapGoogle
        onMarkerClick={handleMarkerClick}
        routePath={null}
        centerMapPosition={centerMapPosition}
        vehicles={vehicles}
        focusVehicle={focusVehicle}
        userLocation={userLocation}
        nearbyStops={nearbyStops}
        tripOverlay={tripOverlay}
        tripOverlayLoading={tripOverlayLoading}
        tripOverlayError={tripOverlayError}
        onVehicleClick={handleVehicleMarkerClick}
        // Passa filtros para exibir apenas a linha/veículo desejado
        visibleRouteShortNames={trackingRouteShortNames}
        visibleVehicleId={focusedVehicleId}
      />

      <BottomSheet
        isOpen={sheetContent !== null && sheetContent !== "navegandoRota"}
        onClose={handleCloseSheet}
        height={sheetHeight}
      >
        {renderSheetContent()}
      </BottomSheet>

      <FloatingNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onNearbyStopsClick={handleNearbyStopsClick}
        nearbyStopsLoading={nearbyStopsLoading}
        nearbyStopsActive={
          nearbyStops.length > 0 && !nearbyStopsLoading && !nearbyStopsError
        }
      />
    </div>
  );
}

export default PlannerPage;
